import { AsYouType, type CountryCode, validatePhoneNumberLength } from 'libphonenumber-js/min';

import { COUNTRIES, Country } from '@/constants/countries-phones';

export const MAX_PHONE_LOCAL_LENGTH = 13;

// `AsYouType` groups a national number only when the country's trunk prefix is present, which users
// omit. Formatting runs with the prefix prepended and the prefix digit removed from the result, so
// the grouping survives without the display ever showing a digit the user did not type.
const TRUNK_PREFIX_CANDIDATES = ['0', '8'];

export interface FormattedLocalPhoneNumber {
  display: string;
  nationalNumber: string;
}

const digitsOf = (value: string): string => value.replace(/\D/g, '');

const isGrouped = (display: string): boolean => /[^0-9]/.test(display);

/** Keeps each country's own digit grouping but renders every separator as a dash. */
const toDisplaySeparators = (formatted: string): string =>
  formatted.replace(/[^0-9]+/g, '-').replace(/^-+|-+$/g, '');

const runAsYouType = (
  digits: string,
  iso2: string,
): { display: string; nationalNumber: string } => {
  try {
    const formatter = new AsYouType(iso2.toUpperCase() as CountryCode);
    const display = toDisplaySeparators(formatter.input(digits));
    // `getNationalNumber()` is missing from the `libphonenumber-js/min` typings; `getNumber()`
    // carries the same value and is declared there.
    return { display, nationalNumber: formatter.getNumber()?.nationalNumber ?? '' };
  } catch {
    return { display: digits, nationalNumber: digits };
  }
};

const withoutLeadingDigits = (formatted: string, count: number): string => {
  let remaining = count;
  let index = 0;
  while (remaining > 0 && index < formatted.length) {
    if (formatted[index] >= '0' && formatted[index] <= '9') remaining -= 1;
    index += 1;
  }
  return formatted.slice(index).replace(/^-+/, '');
};

const groupedWithoutTrunkPrefix = (digits: string, iso2: string): string | null => {
  for (const candidate of TRUNK_PREFIX_CANDIDATES) {
    const prefixed = runAsYouType(candidate + digits, iso2);
    if (prefixed.nationalNumber !== digits || !isGrouped(prefixed.display)) continue;
    const display = withoutLeadingDigits(prefixed.display, candidate.length);
    if (digitsOf(display) === digits) return display;
  }
  return null;
};

export const formatLocalPhoneNumber = (
  value: string,
  iso2: string | null | undefined,
): FormattedLocalPhoneNumber => {
  const digits = digitsOf(value);
  if (!digits) return { display: '', nationalNumber: '' };
  if (!iso2) return { display: digits, nationalNumber: digits };

  const base = runAsYouType(digits, iso2);
  const nationalNumber = base.nationalNumber || digits;

  if (isGrouped(base.display) && digitsOf(base.display) === digits) {
    return { display: base.display, nationalNumber };
  }

  return {
    display: groupedWithoutTrunkPrefix(digits, iso2) ?? digits,
    nationalNumber,
  };
};

const isTooLongForCountry = (digits: string, iso2: string): boolean => {
  try {
    return validatePhoneNumberLength(digits, iso2.toUpperCase() as CountryCode) === 'TOO_LONG';
  } catch {
    return false;
  }
};

export const clampLocalPhoneDigits = (value: string, iso2: string | null | undefined): string => {
  let clamped = digitsOf(value).slice(0, MAX_PHONE_LOCAL_LENGTH);
  if (!iso2) return clamped;
  while (clamped && isTooLongForCountry(clamped, iso2)) {
    clamped = clamped.slice(0, -1);
  }
  return clamped;
};

export const countDigitsBefore = (text: string, caretIndex: number): number =>
  digitsOf(text.slice(0, Math.max(0, caretIndex))).length;

export const caretIndexForDigitCount = (formatted: string, digitCount: number): number => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (formatted[index] >= '0' && formatted[index] <= '9') {
      seen += 1;
      if (seen === digitCount) return index + 1;
    }
  }
  return formatted.length;
};

export const parsePhoneNumber = (
  phoneNumber: string,
): { country: Country | null; localNumber: string } => {
  if (!phoneNumber || !phoneNumber.startsWith('+')) {
    return { country: null, localNumber: phoneNumber || '' };
  }

  const localNumber = phoneNumber.slice(1);
  const matchingCountries = COUNTRIES.filter((country) => localNumber.startsWith(country.dialCode));

  if (matchingCountries.length === 0) {
    return { country: null, localNumber: phoneNumber };
  }

  if (matchingCountries.length === 1) {
    const country = matchingCountries[0];
    return { country, localNumber: localNumber.slice(country.dialCode.length) };
  }

  for (const country of matchingCountries) {
    const phoneWithoutDialCode = localNumber.slice(country.dialCode.length);
    if (
      country.areaCodes?.length &&
      country.areaCodes.some((areaCode) => phoneWithoutDialCode.startsWith(areaCode))
    ) {
      return { country, localNumber: phoneWithoutDialCode };
    }
  }

  const country = matchingCountries[0];
  return { country, localNumber: localNumber.slice(country.dialCode.length) };
};
